import { z } from 'zod';
import { CoordsSchema, PseudonymSchema } from './common';
import { VibeTagSchema } from './profile';

/** A person visible on your Radar radar within a Spot. */
export const NearbyPersonSchema = z.object({
  /** Per-Spot opaque id — NOT the user id. Rotates when they leave. */
  pseudoId: z.string(),
  pseudonym: PseudonymSchema,
  /** Coarse distance bucket — we never expose raw metres. */
  distance: z.enum(['here', 'close', 'near', 'far']),
  /** Approx metres, rounded to 5, for ordering only. */
  approxM: z.number().int().nonnegative(),
  /** Bearing from the viewer in degrees (0 = the viewer's facing), or null. */
  bearing: z.number().gte(0).lt(360).nullable(),
  /**
   * True when the two GPS fixes are too rough to trust the exact
   * distance/bearing — the UI softens the label and blurs the blip. `distance`
   * is never reported finer than "close" in this case.
   */
  approximate: z.boolean(),
  appearanceBlurb: z.string().max(80).nullable(),
  section: z.string().max(24).nullable(),
  tags: z.array(VibeTagSchema).max(5),
  vibeEmoji: z.string().max(8),
});
export type NearbyPerson = z.infer<typeof NearbyPersonSchema>;

/* POST /api/presence/update — heartbeat while checked in + Glow On. */
export const PresenceUpdateSchema = z.object({
  spotId: z.string(),
  coords: CoordsSchema,
  glowOn: z.boolean(),
  /** Minutes from now that Glow should stay on. Ignored when glowOn=false. */
  glowMinutes: z.number().int().min(5).max(180).optional(),
  appearanceBlurb: z.string().max(80).optional(),
  section: z.string().max(24).optional(),
});
export type PresenceUpdate = z.infer<typeof PresenceUpdateSchema>;

export const RadarResponseSchema = z.object({
  spotId: z.string(),
  glowOn: z.boolean(),
  glowExpiresAt: z.iso.datetime().nullable(),
  /**
   * False when the last reported position is outside the Spot's geofence. The
   * client shows a "you've left" notice and, after a few consecutive misses,
   * drops the check-in. While false the server also forces Glow off.
   */
  inArea: z.boolean(),
  people: z.array(NearbyPersonSchema),
});
export type RadarResponse = z.infer<typeof RadarResponseSchema>;
